import React from 'react';
import { Box, Paper, Typography, Container, Button, Link as MuiLink } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PublicLayout } from '../components/PublicLayout';
import { useTheme } from '@mui/material/styles';
import { createGradientTitleStyles } from '../theme/utils';

export const PrivacyPolicy: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  return (
    <PublicLayout>
      <Container maxWidth="md">
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            mb: 4,
          }}
        >
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{ mb: 3 }}
          >
            {t('common.back', 'Volver')}
          </Button>

          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom 
            sx={{ 
              mb: 2, 
              ...createGradientTitleStyles(theme),
            }}
          >
            {t('privacy.title', 'Política de Privacidad')}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 4, fontStyle: 'italic' }}>
            {t('privacy.lastUpdated', 'Última actualización: Enero 2025')}
          </Typography>

          <Box sx={{ '& > *': { mb: 4 } }}>
            <Typography variant="body1" paragraph sx={{ fontWeight: 500 }}>
              {t('privacy.intro',
                'En cumplimiento del Reglamento General de Protección de Datos (RGPD) y la Ley Orgánica de Protección de Datos y Garantía de los Derechos Digitales (LOPDGDD), Cervellia Technologies S.L informa a los usuarios del sitio web sobre su política de protección de datos de carácter personal.')}
            </Typography>

            <section>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                {t('privacy.section1.title', '1. Responsable del Tratamiento')}
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2, listStyle: 'none' }}>
                <Box component="li" sx={{ mb: 1.5 }}>
                  <Typography variant="body1" component="span" sx={{ fontWeight: 600 }}>
                    {t('privacy.section1.denomination', 'Denominación social:')}
                  </Typography>
                  <Typography variant="body1" component="span"> Cervellia Technologies S.L</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1.5 }}>
                  <Typography variant="body1" component="span" sx={{ fontWeight: 600 }}>
                    {t('privacy.section1.nif', 'NIF:')}
                  </Typography>
                  <Typography variant="body1" component="span"> {t('privacy.section1.nifValue', 'En proceso de constitución')}</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1.5 }}>
                  <Typography variant="body1" component="span" sx={{ fontWeight: 600 }}>
                    {t('privacy.section1.address', 'Domicilio social:')}
                  </Typography>
                  <Typography variant="body1" component="span"> {t('privacy.section1.addressValue', 'En proceso de constitución')}</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1.5 }}>
                  <Typography variant="body1" component="span" sx={{ fontWeight: 600 }}>
                    {t('privacy.section1.email', 'Correo electrónico:')}
                  </Typography>
                  <MuiLink href="mailto:info@cervellia.es" sx={{ ml: 0.5 }}>info@cervellia.es</MuiLink>
                </Box>
                <Box component="li" sx={{ mb: 1.5 }}>
                  <Typography variant="body1" component="span" sx={{ fontWeight: 600 }}>
                    {t('privacy.section1.phone', 'Teléfono:')}
                  </Typography>
                  <Typography variant="body1" component="span"> {t('privacy.section1.phoneValue', 'En proceso de constitución')}</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1.5 }}>
                  <Typography variant="body1" component="span" sx={{ fontWeight: 600 }}>
                    {t('privacy.section1.dpo', 'Delegado de Protección de Datos (DPO):')}
                  </Typography>
                  <Typography variant="body1" component="span"> {t('privacy.section1.dpoValue', 'En proceso de constitución')}</Typography>
                </Box>
              </Box>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                {t('privacy.section2.title', '2. Datos Personales que Recogemos')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('privacy.section2.intro', 'En Cervellia Technologies S.L tratamos los siguientes tipos de datos personales:')}
              </Typography>
              
              <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600, mb: 1.5, mt: 2 }}>
                {t('privacy.section2.subsection1', '2.1. Datos recogidos a través del formulario de contacto')}
              </Typography>
              <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
                <li>{t('privacy.section2.item1', 'Nombre completo: Para identificar al usuario y personalizar la comunicación.')}</li>
                <li>{t('privacy.section2.item2', 'Correo electrónico: Para responder a consultas y mantener comunicación.')}</li>
                <li>{t('privacy.section2.item3', 'Teléfono: Para contactar telefónicamente si es necesario.')}</li>
                <li>{t('privacy.section2.item4', 'Empresa: Para contextualizar la consulta y ofrecer servicios adecuados.')}</li>
                <li>{t('privacy.section2.item5', 'Mensaje: Contenido de la consulta o solicitud del usuario.')}</li>
              </Typography>

              <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600, mb: 1.5, mt: 2 }}>
                {t('privacy.section2.subsection2', '2.2. Datos recogidos automáticamente')}
              </Typography>
              <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
                <li>{t('privacy.section2.item6', 'Datos de navegación: Dirección IP, tipo de navegador, páginas visitadas, tiempo de permanencia.')}</li>
                <li>
                  {t('privacy.section2.item7', 'Cookies: Para más información, consulte nuestra')}
                  {' '}
                  <MuiLink
                    component="button"
                    variant="body1"
                    onClick={() => navigate('/cookie-policy')}
                    sx={{
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      fontWeight: 500,
                      color: 'secondary.main',
                    }}
                  >
                    {t('privacy.section2.cookiePolicy', 'Política de Cookies')}
                  </MuiLink>.
                </li>
              </Typography>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                {t('privacy.section3.title', '3. Finalidad del Tratamiento')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('privacy.section3.intro', 'Los datos personales recogidos se utilizan para las siguientes finalidades:')}
              </Typography>
              <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
                <li>{t('privacy.section3.item1', 'Gestión de consultas y solicitudes: Responder a las consultas realizadas a través del formulario de contacto.')}</li>
                <li>{t('privacy.section3.item2', 'Comunicación comercial: Enviar información sobre nuestros servicios, siempre con el consentimiento del usuario.')}</li>
                <li>{t('privacy.section3.item3', 'Mejora del sitio web: Analizar el uso del sitio web para mejorar la experiencia del usuario.')}</li>
                <li>{t('privacy.section3.item4', 'Cumplimiento legal: Cumplir con las obligaciones legales aplicables.')}</li>
              </Typography>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                {t('privacy.section4.title', '4. Base Legal del Tratamiento')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('privacy.section4.intro', 'El tratamiento de datos personales se basa en:')}
              </Typography>
              <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
                <li>{t('privacy.section4.item1', 'Consentimiento del interesado: Para el envío de comunicaciones comerciales y el uso de cookies no esenciales.')}</li>
                <li>{t('privacy.section4.item2', 'Ejecución de medidas precontractuales: Para responder a consultas y solicitudes de información sobre nuestros servicios.')}</li>
                <li>{t('privacy.section4.item3', 'Interés legítimo: Para la mejora del sitio web y análisis de uso (siempre respetando los derechos del usuario).')}</li>
                <li>{t('privacy.section4.item4', 'Cumplimiento de obligaciones legales: Cuando sea necesario para cumplir con la legislación aplicable.')}</li>
              </Typography>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                {t('privacy.section5.title', '5. Conservación de los Datos')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('privacy.section5.intro', 'Los datos personales se conservarán durante:')}
              </Typography>
              <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
                <li>{t('privacy.section5.item1', 'Datos del formulario de contacto: Hasta que el usuario solicite su supresión o durante el tiempo necesario para gestionar la consulta y un período adicional de 3 años para posibles reclamaciones.')}</li>
                <li>{t('privacy.section5.item2', 'Datos para comunicaciones comerciales: Hasta que el usuario retire su consentimiento o solicite la baja.')}</li>
                <li>{t('privacy.section5.item3', 'Datos de navegación: Según lo establecido en nuestra Política de Cookies.')}</li>
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                {t('privacy.section5.note',
                  'Una vez finalizado el período de conservación, los datos serán eliminados de forma segura, salvo que exista una obligación legal de conservación.')}
              </Typography>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                {t('privacy.section6.title', '6. Destinatarios de los Datos')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('privacy.section6.intro',
                  'Los datos personales no serán comunicados a terceros, salvo:')}
              </Typography>
              <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
                <li>{t('privacy.section6.item1', 'Cuando sea necesario para la prestación del servicio (proveedores de hosting, servicios de email, etc.), siempre con garantías adecuadas.')}</li>
                <li>{t('privacy.section6.item2', 'Cuando exista una obligación legal.')}</li>
                <li>{t('privacy.section6.item3', 'Cuando el usuario haya dado su consentimiento expreso.')}</li>
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2, fontStyle: 'italic' }}>
                {t('privacy.section6.note',
                  'En ningún caso se realizarán transferencias internacionales de datos sin las garantías adecuadas conforme al RGPD.')}
              </Typography>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                {t('privacy.section7.title', '7. Derechos del Usuario')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('privacy.section7.intro',
                  'De conformidad con el RGPD y la LOPDGDD, el usuario tiene derecho a:')}
              </Typography>
              <Typography component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><strong>{t('privacy.section7.item1', 'Acceso:')}</strong> {t('privacy.section7.item1Desc', 'Obtener información sobre sus datos personales que estamos tratando.')}</li>
                <li><strong>{t('privacy.section7.item2', 'Rectificación:')}</strong> {t('privacy.section7.item2Desc', 'Solicitar la corrección de datos inexactos o incompletos.')}</li>
                <li><strong>{t('privacy.section7.item3', 'Supresión ("Derecho al Olvido"):')}</strong> {t('privacy.section7.item3Desc', 'Solicitar la eliminación de sus datos cuando ya no sean necesarios.')}</li>
                <li><strong>{t('privacy.section7.item4', 'Oposición:')}</strong> {t('privacy.section7.item4Desc', 'Oponerse al tratamiento de sus datos para fines específicos.')}</li>
                <li><strong>{t('privacy.section7.item5', 'Limitación del tratamiento:')}</strong> {t('privacy.section7.item5Desc', 'Solicitar la limitación del tratamiento en determinadas circunstancias.')}</li>
                <li><strong>{t('privacy.section7.item6', 'Portabilidad:')}</strong> {t('privacy.section7.item6Desc', 'Recibir sus datos en un formato estructurado y de uso común.')}</li>
                <li><strong>{t('privacy.section7.item7', 'Retirar el consentimiento:')}</strong> {t('privacy.section7.item7Desc', 'En cualquier momento, sin afectar a la licitud del tratamiento basado en el consentimiento previo a su retirada.')}</li>
              </Typography>
              <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                <Typography variant="body1" paragraph sx={{ fontWeight: 600, mb: 1 }}>
                  {t('privacy.section7.contact', 'Para ejercer estos derechos, el usuario puede dirigirse a:')}
                </Typography>
                <Typography variant="body1" component="div">
                  <strong>{t('privacy.section7.company', 'Cervellia Technologies S.L')}</strong>
                  <br />
                  {t('privacy.section7.emailLabel', 'Correo electrónico:')} <MuiLink href="mailto:info@cervellia.es">info@cervellia.es</MuiLink>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                  {t('privacy.section7.note',
                    'Debe indicar claramente qué derecho desea ejercer y aportar una copia de su DNI o documento identificativo.')}
                </Typography>
              </Box>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                {t('privacy.section8.title', '8. Medidas de Seguridad')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('privacy.section8.content',
                  'Cervellia Technologies S.L ha adoptado las medidas técnicas y organizativas necesarias para garantizar la seguridad de los datos personales y evitar su alteración, pérdida, tratamiento o acceso no autorizado, teniendo en cuenta el estado de la tecnología, la naturaleza de los datos almacenados y los riesgos a que están expuestos.')}
              </Typography>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                {t('privacy.section9.title', '9. Reclamaciones')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('privacy.section9.content',
                  'Si el usuario considera que el tratamiento de sus datos personales no se ajusta a la normativa vigente, puede presentar una reclamación ante la Agencia Española de Protección de Datos (AEPD):')}
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li><strong>{t('privacy.section9.address', 'Dirección:')}</strong> C/ Jorge Juan, 6, 28001 Madrid</li>
                <li><strong>{t('privacy.section9.phone', 'Teléfono:')}</strong> 912 663 517</li>
                <li><strong>{t('privacy.section9.web', 'Web:')}</strong> <MuiLink href="https://www.aepd.es" target="_blank" rel="noopener noreferrer">www.aepd.es</MuiLink></li>
              </Box>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                {t('privacy.section10.title', '10. Modificaciones de la Política de Privacidad')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('privacy.section10.content',
                  'Cervellia Technologies S.L se reserva el derecho de modificar esta Política de Privacidad para adaptarla a novedades legislativas, jurisprudenciales o interpretativas de la Agencia Española de Protección de Datos. En tal caso, se anunciarán en esta página los cambios introducidos con la debida antelación a su puesta en práctica.')}
              </Typography>
            </section>

            <section>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                {t('privacy.section11.title', '11. Contacto')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('privacy.section11.content',
                  'Para cualquier consulta relacionada con esta Política de Privacidad o el tratamiento de datos personales, puede contactarnos en:')}
              </Typography>
              <Typography variant="body1">
                <strong>{t('privacy.section11.emailLabel', 'Correo electrónico:')}</strong>{' '}
                <MuiLink href="mailto:info@cervellia.es">info@cervellia.es</MuiLink>
              </Typography>
            </section>
          </Box>
        </Paper>
      </Container>
    </PublicLayout>
  );
};

